/* ============================================================
   driftgrain.io — site.ts

   Compiled to static/js/site.js by `bun run ts`. Edit this file, never
   the output — the output is gitignored and overwritten on every build.

   Everything here is progressive enhancement. With JS disabled
   the site still renders, still navigates, and still reads.
   Nine jobs: theme, the ledger clock, nav state after an htmx
   page swap, toasts, the range slider's fill position, opening
   dialogs, closing the drawer, dismissing a tooltip, and keeping
   an accordion to one open panel.

   Written as a plain script rather than a module: no imports, no exports,
   so tsc emits it unwrapped and <script defer> loads it with no loader.
   ============================================================ */

interface Window {
  /** Raise a toast. Exposed by site.ts; see job 4 below. */
  dgToast: (message: string, options?: { tone?: "default" | "success" | "warning" | "danger"; duration?: number }) => HTMLElement;
}

(function () {
  "use strict";

  const root = document.documentElement;

  type Theme = "dark" | "light";

  /* ---------- 1. theme ----------------------------------------------------
     Dark is the default (set inline in <head> so there's no flash).
     The toggle writes a preference; if storage is unavailable we just
     don't remember it, which is a fine failure.

     Flipping data-theme on <html> re-resolves the --dg-* tokens, and
     because mini.css's own custom properties are defined as var(--dg-*)
     they follow along. One attribute, both systems. */

  function store(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode: ignore */
    }
  }

  function setTheme(mode: Theme): void {
    root.setAttribute("data-theme", mode);
    store("dg-theme", mode);
    const next: Theme = mode === "dark" ? "light" : "dark";

    // Title case, and the same two words everywhere: the drawer label, the
    // tooltip and the accessible name are three renderings of one string.
    const nextLabel = next === "light" ? "Light Mode" : "Dark Mode";

    document.querySelectorAll<HTMLElement>("[data-theme-toggle]").forEach((btn) => {
      // The accessible name, set first and unconditionally, because in the
      // icon-only form there is no text to fall back on.
      //
      // It must CONTAIN the visible label word for word. In the drawer the
      // button reads "Light Mode" on screen while its name is "Switch to Light
      // Mode" — WCAG 2.5.3 (Label in Name) asks that the name include the
      // visible text, so that someone saying "click Light Mode" to a voice
      // control is understood. Build the name FROM the label and the two
      // cannot drift apart.
      btn.setAttribute("aria-label", "Switch to " + nextLabel);

      // Same string again for the tooltip, so the bubble and the name agree
      // even if a screen reader happens to announce generated content.
      if (btn.hasAttribute("data-tooltip")) {
        btn.setAttribute("data-tooltip", "Switch to " + nextLabel);
      }

      // The toggle carries a glyph, a spelled-out label, or neither, and each
      // part is updated where it exists.
      //
      // The textContent fallback is last and guarded on BOTH being absent,
      // which is not defensive padding: writing textContent on the icon form
      // destroys the <span> that carries the glyph, and it would do it on the
      // first call, which runs at DOMContentLoaded. The icon would vanish
      // before anyone ever clicked anything.
      const icon = btn.querySelector(".ti");
      const label = btn.querySelector(".theme-toggle-label");

      // Every one of them advertises where the button GOES, not where it is: a
      // sun and the words "Light Mode" mean pressing this gets you the light
      // one. Naming a toggle after its current state is the classic way to
      // make it ambiguous.
      if (icon) {
        icon.classList.toggle("ti-sun", next === "light");
        icon.classList.toggle("ti-moon", next === "dark");
      }
      if (label) {
        label.textContent = nextLabel;
      }
      if (!icon && !label) {
        btn.textContent = nextLabel;
      }
    });
  }

  document.addEventListener("click", (e) => {
    // e.target is EventTarget, which has no closest(). It is an Element for
    // any click that lands on rendered content, but the type has to say so.
    const target = e.target as Element | null;
    const btn = target?.closest("[data-theme-toggle]");
    if (!btn) return;
    setTheme(root.getAttribute("data-theme") === "dark" ? "light" : "dark");
  });

  /* ---------- 2. the ledger clock -----------------------------------------
     The signature element is a public record, so the numbers in it have to
     be real and current. Anything with data-ledger recomputes on load and
     after any htmx swap. Dates come from the markup, never from here.

     The bar is a native <progress>, so setting .value is the whole update —
     it reports its own position to assistive tech without any ARIA. */

  const DAY = 86400000;

  function fmtInt(n: number): string {
    return n.toLocaleString("en-US");
  }

  function updateLedgers(scope: ParentNode | null): void {
    (scope ?? document).querySelectorAll<HTMLElement>("[data-ledger]").forEach((el) => {
      // dataset values are `string | undefined`, and Date.parse wants a
      // string. An absent attribute parses to NaN, which the guard below
      // already had to handle — so the fallback keeps that one path.
      const start = Date.parse(el.dataset.start ?? "");
      const target = Date.parse(el.dataset.target ?? "");
      if (isNaN(start) || isNaN(target) || target <= start) return;

      const now = Date.now();
      const daysLeft = Math.max(0, Math.ceil((target - now) / DAY));
      const pct = Math.min(100, Math.max(0, ((now - start) / (target - start)) * 100));

      const days = el.querySelector("[data-ledger-days]");
      const bar = el.querySelector<HTMLProgressElement>("[data-ledger-bar]");
      if (days) days.textContent = fmtInt(daysLeft);
      if (bar) bar.value = pct;
    });
  }

  /* ---------- 3. (removed) nav state after an htmx swap -------------------
     There used to be a markCurrentNav() here, reapplying the current-page
     marker after every boosted navigation because hx-boost replaces #main and
     never the header.

     It is gone because the header's <nav> now carries hx-boost="false": a nav
     click is a full page load, the server renders the marker, and it cannot go
     stale. One rule, in one place, in the template. */

  /* ---------- 5. range fill ----------------------------------------------
     The track gradient is CSS (see _controls.scss). All this does is tell it
     where the thumb is, as a percentage, so the gradient can be revealed up
     to that point rather than across the whole track.

     There is no CSS-only way to do this in both engines — Firefox has
     ::-moz-range-progress and WebKit has no equivalent — so it is one custom
     property and nothing else. With JS off the property is never set and the
     `100%` fallback in the stylesheet shows the full gradient track, which is
     a perfectly good slider. Nothing here is required for the control to
     work; the input is native either way. */

  function paintRange(el: HTMLInputElement): void {
    const min = Number(el.min || 0);
    const max = Number(el.max || 100);
    const span = max - min;
    // A zero or backwards span would divide by zero. Full track is the same
    // answer the no-JS path gives, so the failure mode stays consistent.
    const pct = span > 0 ? ((Number(el.value) - min) / span) * 100 : 100;
    el.style.setProperty("--dg-range-pos", Math.min(100, Math.max(0, pct)) + "%");
  }

  function paintRanges(scope: ParentNode | null): void {
    (scope ?? document).querySelectorAll<HTMLInputElement>('input[type="range"]').forEach(paintRange);
  }

  // Delegated, so a slider that arrives in an htmx fragment is already wired
  // and only its initial paint has to be done by init().
  document.addEventListener("input", (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.type === "range") paintRange(el);
  });

  /* ---------- 7. closing the drawer -------------------------------------
     A checkbox drawer cannot close itself. Nothing in CSS can uncheck a box,
     and hx-boost replaces #main while leaving the header — and the checkbox —
     exactly as they were. So following a link out of the drawer left it open
     over the page you had just asked for.

     That one stuck checkbox was doing more damage than it looked. It also
     matched `html:has(input.drawer:checked) { overflow: hidden }`, the scroll
     lock in _drawer.scss, which put `overflow: hidden` on the document — and
     `position: sticky` resolves against the nearest scrolling ancestor, so
     with the document no longer scrolling, the sticky header had nothing to
     stick to and scrolled away with the page. The header falling out of view
     and the drawer staying open were the same bug.

     Deliberately NOT called from init(): that runs after every htmx swap,
     including the news fragments and the promo rail, and a drawer has no
     business closing because something unrelated loaded. It closes when you
     follow a link out of one, and on load, which is where a browser restoring
     form state would otherwise hand you an open drawer. */

  function closeDrawers(): void {
    document.querySelectorAll<HTMLInputElement>("input.drawer:checked").forEach((box) => {
      box.checked = false;
    });
  }

  document.addEventListener("click", (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    // The selector does the work: an <a href> that is a descendant of the
    // panel belonging to a checked drawer. Anything else on the page misses.
    if (target.closest("input.drawer:checked + * a[href]")) closeDrawers();
  });

  /* ---------- 8. dismissing a tooltip ------------------------------------
     WCAG 1.4.13 asks that content shown on hover or focus can be dismissed
     "without moving pointer hover or keyboard focus". A CSS-only tooltip can
     satisfy Hoverable and Persistent on its own — see section 12 of main.scss
     — but not that one: nothing in CSS observes a keypress.

     So this is the whole of it. Escape sets an attribute on <html>, the
     stylesheet hides every bubble while it is there, and the next pointer move
     or focus change clears it so the following tooltip behaves normally. The
     pointer sitting still on the trigger fires nothing, which is exactly the
     required behaviour: dismissed, and it stays dismissed until you move.

     The listener never calls preventDefault, so Escape still closes a native
     <dialog> and still leaves a form field, both of which are the platform's
     to handle.

     With this script absent the attribute is never set, the CSS rule never
     matches, and the tooltip is a plain hover-and-focus bubble. */

  let tooltipsOff = false;

  function setTooltipsOff(off: boolean): void {
    // Guarded because the restore path runs on pointermove. Touching the DOM
    // on every mouse move for an attribute that is almost always already
    // absent is the kind of thing that shows up in a scroll jank profile.
    if (off === tooltipsOff) return;
    tooltipsOff = off;
    if (off) root.setAttribute("data-tooltips", "off");
    else root.removeAttribute("data-tooltips");
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setTooltipsOff(true);
  });

  // Capture, so a handler that stops propagation somewhere in the page cannot
  // leave tooltips switched off for the rest of the session.
  (["pointermove", "pointerdown", "focusin"] as const).forEach((type) => {
    document.addEventListener(type, () => {
      setTooltipsOff(false);
    }, true);
  });

  /* ---------- 9. accordions: one panel open at a time ---------------------
     An .accordion is EXCLUSIVE by default. Opening a panel closes the one that
     was open, in both structures, with nothing for the author to remember.

     THE TWO HALVES ARE NOT DONE THE SAME WAY, and that is deliberate.

     <details> has had this natively since 2024: give a group of them the same
     `name` and the browser enforces it — on click, on find-in-page, and on a
     scripted .open = true. Verified in Chromium 151: two marked `open` at parse
     collapse to the first, and a sibling in a different group is untouched. So
     the job here is only to HAND OUT the name where the markup did not, which
     is what makes exclusivity the default rather than something you opt into.
     Where the name is written in the markup — content/faq.md does — the group
     is exclusive with this script disabled, and that behaviour is not something
     to give up for the sake of one code path.

     The checkbox structure has no equivalent. Radios would be exclusive with no
     script at all, but a radio cannot be unchecked by clicking it again: the
     open panel could only ever be REPLACED, never closed, and the two
     structures would stop behaving alike. That is the property the component
     exists to have, so this half is enforced here instead.

     Opt out per accordion with data-accordion="multi".

     With scripts off: a named <details> group stays exclusive, an unnamed one
     and the checkbox form fall back to independent panels — which is what they
     did before any of this, so nothing breaks. */

  let accordionGroups = 0;

  function initAccordions(scope: ParentNode | null): void {
    (scope ?? document).querySelectorAll<HTMLElement>(".accordion").forEach((acc) => {
      if (acc.dataset.accordion === "multi") return;

      // `:scope >` so an accordion nested inside a panel keeps its own group
      // rather than being absorbed into its parent's.
      const panels = acc.querySelectorAll<HTMLElement>(":scope > details");
      // Only the ones the markup left anonymous. An accordion that named itself
      // — content/faq.md does — needs nothing from this and should not come out
      // of it wearing a generated attribute it never asked for.
      const unnamed = [...panels].filter((panel) => !panel.hasAttribute("name"));
      if (unnamed.length) {
        let group = acc.dataset.accordionName;
        if (!group) {
          group = "dg-accordion-" + ++accordionGroups;
          acc.dataset.accordionName = group;
        }
        // setAttribute rather than the .name property: the property is newer
        // than the attribute in lib.dom, and the attribute is what the browser
        // groups on either way.
        unnamed.forEach((panel) => {
          panel.setAttribute("name", group as string);
        });
      }

      // Parity with what the browser does to a named <details> group at parse
      // time: if the markup opened more than one, only the first survives.
      // Without this a checkbox accordion could load with three panels open and
      // only tidy itself up once somebody clicked.
      const toggles = acc.querySelectorAll<HTMLInputElement>(".accordion-toggle");
      let seen = false;
      toggles.forEach((toggle) => {
        if (toggle.closest(".accordion") !== acc || !toggle.checked) return;
        if (seen) toggle.checked = false;
        seen = true;
      });
    });
  }

  // Delegated, so a panel that arrives in an htmx fragment is already wired.
  document.addEventListener("change", (e) => {
    const toggle = e.target;
    if (!(toggle instanceof HTMLInputElement)) return;
    if (!toggle.classList.contains("accordion-toggle") || !toggle.checked) return;

    const acc = toggle.closest<HTMLElement>(".accordion");
    if (!acc || acc.dataset.accordion === "multi") return;

    acc.querySelectorAll<HTMLInputElement>(".accordion-toggle").forEach((other) => {
      // The closest() check keeps a nested accordion's toggles out of it.
      if (other !== toggle && other.closest(".accordion") === acc) other.checked = false;
    });
  });

  /* ---------- wiring ---------- */

  function init(scope: ParentNode | null): void {
    updateLedgers(scope);
    paintRanges(scope);
    initAccordions(scope);
  }

  document.addEventListener("DOMContentLoaded", () => {
    const stored = root.getAttribute("data-theme");
    setTheme(stored === "light" ? "light" : "dark");
    closeDrawers();
    init(document);
  });

  // htmx fires this after any successful swap, including boosted navigation
  // and the news fragments. Same init, scoped to what actually changed.
  //
  // Not in lib.dom, because htmx is not a browser API — hence the cast. The
  // detail carried on the event is unused here; only the swap target is.
  document.addEventListener("htmx:afterSwap", (e) => {
    init((e as CustomEvent).target as ParentNode | null);
  });

  // Recompute at midnight-ish so a tab left open overnight isn't lying.
  setInterval(() => {
    updateLedgers(document);
  }, 60 * 60 * 1000);

  /* ---------- 4. toasts --------------------------------------------------
     mini.css styles `.toast` and stops there. This is the rest of it: a
     region to stack them in, a dismiss control, and a timer that can be
     paused, extended or turned off.

     Raise one either way:

       <button data-toast="Saved">Save</button>
       <button data-toast="Gone" data-toast-tone="danger" data-toast-duration="0">

       dgToast("Copied to the clipboard");
       dgToast("Could not reach the server", { tone: "danger", duration: 0 });

     ACCESSIBILITY, and the reason the region is built at startup rather than
     on first use: a screen reader announces changes to a live region it was
     already watching. A region that appears at the same instant as its first
     message is frequently missed entirely.

     The region is `polite`, which is right for everything this site has to
     say. If an assertive one is ever needed, add a SECOND region with
     role="alert" — do not flip aria-live on an existing one, because support
     for that is inconsistent and it is the kind of bug nobody catches. */

  type ToastTone = "default" | "success" | "warning" | "danger";

  interface ToastOptions {
    tone?: ToastTone;
    /** Milliseconds before it leaves on its own. 0 keeps it until dismissed. */
    duration?: number;
  }

  const TOAST_DEFAULT_DURATION = 5000;
  // Beyond this the oldest goes, so a loop cannot paper over the screen.
  const TOAST_MAX_VISIBLE = 4;

  let toastRegion: HTMLElement | null = null;

  function getToastRegion(): HTMLElement {
    if (toastRegion) return toastRegion;
    const region = document.createElement("div");
    region.className = "toast-region";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    // The toasts are announced; the container itself is not a landmark.
    region.setAttribute("aria-relevant", "additions");
    document.body.appendChild(region);
    toastRegion = region;
    return region;
  }

  /* Two ways out, and they are not the same event.

     DISMISSED means someone asked for it to go. That is an instruction, and an
     instruction should land on the frame it was given — waiting out a 200ms
     fade after a click reads as lag, not polish. So it goes now.

     EXPIRED means the timer ran out with nobody watching. Nothing is waiting
     on it, so it fades, which is what stops four stacked toasts from blinking
     out of existence. */
  function removeToast(toast: HTMLElement, immediate = false): void {
    if (toast.dataset.leaving === "1") return;
    toast.dataset.leaving = "1";

    if (immediate) {
      toast.remove();
      return;
    }

    toast.classList.add("toast--leaving");

    // Wait for the leave animation rather than guessing at its length. Under
    // prefers-reduced-motion the animation is ~0ms and this still fires, so
    // there is no branch for it. The timeout is the belt-and-braces path for
    // the case where the element is detached before the event arrives.
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      toast.remove();
    };
    toast.addEventListener("animationend", finish, { once: true });
    setTimeout(finish, 1000);
  }

  function dgToast(message: string, options: ToastOptions = {}): HTMLElement {
    const region = getToastRegion();
    const tone = options.tone ?? "default";
    const duration = options.duration ?? TOAST_DEFAULT_DURATION;

    while (region.children.length >= TOAST_MAX_VISIBLE) {
      const oldest = region.firstElementChild;
      if (!(oldest instanceof HTMLElement)) break;
      oldest.remove();
    }

    const toast = document.createElement("div");
    toast.className = "toast" + (tone === "default" ? "" : " toast--" + tone);

    const text = document.createElement("span");
    text.className = "toast__text";
    text.textContent = message;
    toast.appendChild(text);

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.className = "toast__dismiss";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.textContent = "\u00D7";
    dismiss.addEventListener("click", () => {
      removeToast(toast, true);
    });
    toast.appendChild(dismiss);

    region.appendChild(toast);

    if (duration > 0) {
      let timer = window.setTimeout(() => {
        removeToast(toast);
      }, duration);

      // WCAG 2.2.1: a message on a timer has to be stoppable. Hovering or
      // tabbing into it holds it open, which is what someone does the moment
      // they start reading. Leaving restarts the full duration rather than
      // resuming a stub of it — a toast you just looked away from should not
      // vanish in 200ms.
      const hold = (): void => {
        window.clearTimeout(timer);
      };
      const resume = (): void => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          removeToast(toast);
        }, duration);
      };
      toast.addEventListener("mouseenter", hold);
      toast.addEventListener("mouseleave", resume);
      toast.addEventListener("focusin", hold);
      toast.addEventListener("focusout", resume);
    }

    return toast;
  }

  /* Declarative form. Same data-attribute vocabulary as the theme toggle and
     the ledger, and it survives an htmx swap because the listener is on the
     document rather than on the buttons. */
  document.addEventListener("click", (e) => {
    const target = e.target as Element | null;
    const trigger = target?.closest<HTMLElement>("[data-toast]");
    if (!trigger) return;

    const options: ToastOptions = {
      tone: (trigger.dataset.toastTone as ToastTone | undefined) ?? "default",
    };

    // An absent or unparseable attribute has to leave `duration` UNSET rather
    // than set to undefined — exactOptionalPropertyTypes draws that
    // distinction, and it is the right one here: an unset key takes the
    // default, where NaN would have made the toast permanent by accident.
    if (trigger.dataset.toastDuration !== undefined) {
      const parsed = Number(trigger.dataset.toastDuration);
      if (Number.isFinite(parsed) && parsed >= 0) options.duration = parsed;
    }

    dgToast(trigger.dataset.toast ?? "", options);
  });

  /* ---------- 6. dialogs -------------------------------------------------
     Opening a native <dialog> with showModal() is the only part of a modal
     that needs script. Escape, the close button (a submit inside
     <form method="dialog">) and returning focus to whatever opened it are all
     platform behavior.

       <button data-dialog="dedication">Open</button>
       <dialog id="dedication" class="dialog"> … </dialog>

     Delegated on the document, so a trigger that arrives in an htmx fragment
     is already wired and nothing has to be re-bound after a swap.

     With JS off the trigger does nothing. That is the honest trade for a real
     modal: the checkbox version needs no script and cannot trap focus, and a
     dialog a keyboard user can tab out the back of is worse than one that
     never opened. Nothing on the public site depends on one. */

  document.addEventListener("click", (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest<HTMLElement>("[data-dialog]");
    if (trigger) {
      const dialog = document.getElementById(trigger.dataset.dialog ?? "");
      if (dialog instanceof HTMLDialogElement) {
        e.preventDefault();
        dialog.showModal();
      }
      return;
    }

    // Click outside the box. The dialog has no padding of its own (see
    // _dialog.scss), so `target === the dialog` can only mean the backdrop —
    // no coordinates, no bounding box, no guessing.
    if (target instanceof HTMLDialogElement && target.open) target.close();
  });

  // The one thing this file exposes. Everything else stays inside the IIFE.
  window.dgToast = dgToast;
})();
